// FILE: src/pages/NursingHomeStaff.jsx
// Staff management for a nursing home tenant.
// Deliberately separate from the B2B member UI in TenantDetail.jsx so nursing
// home changes cannot affect the business tenant product. Shares the backend
// tenant invite functions, which contain no NH-specific logic.
import React, { useEffect, useState, useCallback } from "react";
import { getAuth } from "firebase/auth";
import { db } from "../firebase";
import { collection, getDocs, query, where } from "firebase/firestore";

const FN_BASE = "https://us-central1-lastwishbox-business.cloudfunctions.net";

const B = {
  red: "#980000",
  redDark: "#650202",
  orange: "#f15822",
  navy: "#003353",
  steel: "#657899",
  bg: "#f6f7fb",
  white: "#fff",
  border: "#e8ebf3",
};

// NH vocabulary. "hr_admin" is the underlying tenant role that grants resident
// access; it is never shown to the user under that name.
const NH_ROLES = [
  { v: "hr_admin", label: "Staff", hint: "Can view residents, record consent and log reviews" },
  { v: "owner", label: "Manager", hint: "Everything staff can do, plus managing staff" },
];

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: 10,
  border: `1px solid ${B.border}`,
  fontSize: 14,
  outline: "none",
  boxSizing: "border-box",
  fontFamily: "inherit",
};

function roleLabel(role) {
  const r = NH_ROLES.find((x) => x.v === role);
  return r ? r.label : (role || "\u2014");
}

function fmtDate(ms) {
  if (!ms) return "\u2014";
  try { return new Date(Number(ms)).toLocaleDateString("en-IE"); } catch { return "\u2014"; }
}

export default function NursingHomeStaff({ tenantId, isOwner }) {
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [busy, setBusy] = useState("");

  const [showInvite, setShowInvite] = useState(false);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("hr_admin");

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setErr("");
    try {
      const mSnap = await getDocs(collection(db, "tenants", tenantId, "members"));
      setMembers(mSnap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) })));

      try {
        const iSnap = await getDocs(
          query(collection(db, "tenantInvites"), where("tenantId", "==", tenantId))
        );
        setInvites(
          iSnap.docs
            .map((d) => ({ id: d.id, ...(d.data() || {}) }))
            .filter((i) => (i.status || "pending") === "pending")
        );
      } catch {
        setInvites([]);
      }
    } catch (e) {
      console.error("NH staff load failed", e);
      setErr(String(e.message || e));
    }
    setLoading(false);
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  async function callFn(path, body, method = "POST") {
    const user = getAuth().currentUser;
    if (!user) throw new Error("Not signed in");
    const token = await user.getIdToken();
    const r = await fetch(`${FN_BASE}/${path}`, {
      method,
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: method === "POST" ? JSON.stringify(body) : undefined,
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d.error || `Request failed (${r.status})`);
    return d;
  }

  const sendInvite = async () => {
    const addr = email.trim().toLowerCase();
    if (!addr.includes("@")) { setErr("Enter a valid email address"); return; }
    setBusy("invite");
    setErr("");
    setOk("");
    try {
      // Check the address is usable before anything is sent, so problems appear
      // here rather than after the invitee has clicked through and tried to register.
      const check = await callFn("nhCheckStaffEmail", { tenantId, email: addr });
      if (!check.usable) {
        setErr(check.message || "That email address cannot be invited.");
        setBusy("");
        return;
      }

      await callFn("createTenantInvite", { tenantId, email: addr, role });
      setOk(`Invite sent to ${addr}.`);
      setEmail("");
      setRole("hr_admin");
      setShowInvite(false);
      await load();
    } catch (e) {
      console.error("createTenantInvite failed", e);
      setErr(String(e.message || e));
    }
    setBusy("");
  };

  const resend = async (inviteId, to) => {
    setBusy(inviteId);
    setErr(""); setOk("");
    try {
      await callFn("resendTenantInvite", { tenantId, inviteId });
      setOk(`Invite resent to ${to}.`);
    } catch (e) {
      setErr(String(e.message || e));
    }
    setBusy("");
  };

  const revoke = async (inviteId, to) => {
    if (!window.confirm(`Cancel the invite to ${to}?`)) return;
    setBusy(inviteId);
    setErr(""); setOk("");
    try {
      await callFn("revokeTenantInvite", { tenantId, inviteId });
      setOk(`Invite to ${to} cancelled.`);
      await load();
    } catch (e) {
      setErr(String(e.message || e));
    }
    setBusy("");
  };

  const removeMember = async (uid, who) => {
    if (!window.confirm(`Remove ${who} from this home? They will lose access to all resident records.`)) return;
    setBusy(uid);
    setErr(""); setOk("");
    try {
      await callFn("removeTenantMember", { tenantId, memberUid: uid });
      setOk(`${who} removed.`);
      await load();
    } catch (e) {
      setErr(String(e.message || e));
    }
    setBusy("");
  };

  const btn = (bg, color, border) => ({
    padding: "6px 12px", borderRadius: 8,
    border: border || "none", background: bg, color,
    fontWeight: 700, fontSize: 12, cursor: "pointer",
  });

  return (
    <div>
      {err && <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b", borderRadius: 10, padding: "12px 16px", fontSize: 14, fontWeight: 600, marginBottom: 16 }}>{err}</div>}
      {ok && <div style={{ background: "#ecfdf5", border: "1px solid #a7f3d0", color: "#065f46", borderRadius: 10, padding: "12px 16px", fontSize: 14, fontWeight: 600, marginBottom: 16 }}>{ok}</div>}

      {isOwner && (
        <div style={{ marginBottom: 20 }}>
          {!showInvite ? (
            <button onClick={() => setShowInvite(true)}
              style={{ padding: "9px 18px", borderRadius: 10, border: "none", background: B.orange, color: B.white, fontWeight: 700, fontSize: 14, cursor: "pointer" }}
            >Invite a staff member</button>
          ) : (
            <div style={{ background: B.white, border: `1px solid ${B.border}`, borderRadius: 14, padding: "18px 20px" }}>
              <h3 style={{ margin: "0 0 14px", fontSize: 16, color: B.redDark }}>Invite a staff member</h3>

              <div style={{ marginBottom: 14 }}>
                <label style={{ display: "block", fontSize: 13, fontWeight: 700, color: B.redDark, marginBottom: 5 }}>Their work email</label>
                <input style={inputStyle} value={email} onChange={(e) => setEmail(e.target.value)} />
                <div style={{ fontSize: 12, color: B.steel, marginTop: 4 }}>
                  Each staff member needs their own login. Every action they take is recorded against their name.
                </div>
              </div>

              <div style={{ marginBottom: 18 }}>
                <label style={{ display: "block", fontSize: 13, fontWeight: 700, color: B.redDark, marginBottom: 5 }}>Access level</label>
                <select style={inputStyle} value={role} onChange={(e) => setRole(e.target.value)}>
                  {NH_ROLES.map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}
                </select>
                <div style={{ fontSize: 12, color: B.steel, marginTop: 4 }}>
                  {(NH_ROLES.find((r) => r.v === role) || {}).hint}
                </div>
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                <button onClick={() => { setShowInvite(false); setEmail(""); }}
                  style={{ padding: "9px 18px", borderRadius: 10, border: `1px solid ${B.border}`, background: B.white, color: B.steel, fontWeight: 700, fontSize: 14, cursor: "pointer" }}
                >Cancel</button>
                <button onClick={sendInvite} disabled={busy === "invite" || !email.includes("@")}
                  style={{
                    padding: "9px 20px", borderRadius: 10, border: "none", background: B.orange, color: B.white,
                    fontWeight: 700, fontSize: 14,
                    cursor: busy === "invite" || !email.includes("@") ? "default" : "pointer",
                    opacity: busy === "invite" || !email.includes("@") ? .6 : 1,
                  }}>{busy === "invite" ? "Sending\u2026" : "Send invite"}</button>
              </div>
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div style={{ color: B.steel, padding: 20 }}>Loading staff&hellip;</div>
      ) : (
        <>
          {invites.length > 0 && (
            <div style={{ background: B.white, border: `1px solid ${B.border}`, borderRadius: 14, overflow: "hidden", marginBottom: 20 }}>
              <div style={{ padding: "14px 18px", borderBottom: `1px solid ${B.border}` }}>
                <h3 style={{ margin: 0, fontSize: 15, color: B.redDark }}>Pending invites</h3>
              </div>
              {invites.map((inv) => (
                <div key={inv.id} style={{ padding: "12px 18px", borderTop: `1px solid ${B.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
                  <div>
                    <div style={{ fontWeight: 700, color: B.redDark, fontSize: 14 }}>{inv.email}</div>
                    <div style={{ fontSize: 12, color: B.steel, marginTop: 2 }}>
                      {roleLabel(inv.role)} &middot; invited {fmtDate(inv.createdAtMs)}
                    </div>
                  </div>
                  {isOwner && (
                    <div style={{ display: "flex", gap: 8 }}>
                      <button onClick={() => resend(inv.id, inv.email)} disabled={busy === inv.id}
                        style={btn(B.white, B.steel, `1px solid ${B.border}`)}>Resend</button>
                      <button onClick={() => revoke(inv.id, inv.email)} disabled={busy === inv.id}
                        style={btn("#fef2f2", B.red, "1px solid #fecaca")}>Cancel</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <div style={{ background: B.white, border: `1px solid ${B.border}`, borderRadius: 14, overflow: "hidden" }}>
            <div style={{ padding: "14px 18px", borderBottom: `1px solid ${B.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h3 style={{ margin: 0, fontSize: 15, color: B.redDark }}>Staff</h3>
              <span style={{ fontSize: 13, color: B.steel }}>{members.length}</span>
            </div>

            {members.length === 0 ? (
              <div style={{ padding: 26, textAlign: "center", color: B.steel }}>No staff yet.</div>
            ) : members.map((m) => (
              <div key={m.id} style={{ padding: "12px 18px", borderTop: `1px solid ${B.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 700, color: B.redDark, fontSize: 14, wordBreak: "break-all" }}>{m.email || m.id}</div>
                  <div style={{ fontSize: 12, color: B.steel, marginTop: 2 }}>
                    {roleLabel(m.role)}
                    {m.status && m.status !== "active" ? ` \u00b7 ${m.status}` : ""}
                    {m.createdAtMs ? ` \u00b7 since ${fmtDate(m.createdAtMs)}` : ""}
                  </div>
                </div>
                {isOwner && m.role !== "owner" && (
                  <button onClick={() => removeMember(m.id, m.email || m.id)} disabled={busy === m.id}
                    style={btn("#fef2f2", B.red, "1px solid #fecaca")}>Remove</button>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
